export type DomRefs = {
  bluefyGate: HTMLElement;
  bluefyDownloadLink: HTMLAnchorElement;
  updaterShell: HTMLElement;
  eulaGate: HTMLElement;
  eulaContent: HTMLElement;
  eulaAcceptCheckbox: HTMLInputElement;
  eulaAcceptButton: HTMLButtonElement;
  eulaDeclineButton: HTMLButtonElement;
  compatibilityPanel: HTMLElement;
  compatibilityMessage: HTMLElement;
  idleDisconnectHomeMessage: HTMLElement;

  wizardSteps: HTMLElement[];
  taskWizardProgress: HTMLElement;
  wizardProgressFill: HTMLElement;
  wizardProgressLabel: HTMLElement;
  wizardProgressTitle: HTMLElement;

  deviceStatsPanel: HTMLElement;
  connectButton: HTMLButtonElement;
  deviceName: HTMLElement;
  deviceId: HTMLElement;
  deviceFirmware: HTMLElement;
  deviceHardware: HTMLElement;
  deviceSerial: HTMLElement;
  deviceModel: HTMLElement;
  deviceCenterFrequency: HTMLElement;
  devicePower: HTMLElement;
  deviceBattery: HTMLElement;
  deviceTemperature: HTMLElement;
  deviceOdometer: HTMLElement;

  flowFirmware: HTMLInputElement;
  flowRetune: HTMLInputElement;
  flowDiagnostics: HTMLInputElement;
  flowFirmwareBody: HTMLElement;

  taskPanelTitle: HTMLElement;
  taskPanelDescription: HTMLElement;

  statusText: HTMLElement;

  firmwareFlowSection: HTMLElement;
  firmwareSummary: HTMLElement;
  firmwareList: HTMLElement;
  firmwareEmpty: HTMLElement;
  startUpdateButton: HTMLButtonElement;
  cancelButton: HTMLButtonElement;
  reconnectPanel: HTMLElement;
  reconnectButton: HTMLButtonElement;
  progressSection: HTMLElement;
  progressBar: HTMLElement;
  progressMeta: HTMLElement;
  progressPercent: HTMLElement;
  progressSpeed: HTMLElement;
  progressDetail: HTMLElement;

  retuneFlowSection: HTMLElement;
  retuneFirmwareBanner: HTMLElement;
  retuneStatusText: HTMLElement;
  retuneInstruction: HTMLElement;
  retunePrestartWarning: HTMLElement;
  retuneTargetPanel: HTMLElement;
  retuneTargetSummary: HTMLElement;
  retuneTargetOptions: HTMLElement;
  retuneHoldBanner: HTMLElement;
  retuneProgress: HTMLElement;
  retuneProgressBar: HTMLElement;
  retuneProgressLabel: HTMLElement;
  retuneChartCard: HTMLElement;
  retuneChartFrame: HTMLElement;
  retuneRuntimeRail: HTMLElement;
  retuneRuntimeProbeLayer: HTMLElement;
  retuneRuntimeRefineWindow: HTMLElement;
  retuneRuntimeCenterMarker: HTMLElement;
  retuneRuntimeCenterValue: HTMLElement;
  retuneRuntimeRangeMin: HTMLElement;
  retuneRuntimeRangeMax: HTMLElement;
  retuneRuntimePhaseStatus: HTMLElement;
  retuneRuntimePhaseScanning: HTMLElement;
  retuneRuntimePhaseRefining: HTMLElement;
  retuneRuntimePhaseTuned: HTMLElement;
  retuneChartLegend: HTMLElement;
  retuneChartLegendOriginal: HTMLElement;
  retuneRuntimeLegend: HTMLElement;
  retuneRuntimeLegendCenter: HTMLElement;
  retuneChartCanvas: HTMLCanvasElement;
  retuneComparePanel: HTMLElement;
  retuneCompareInstruction: HTMLElement;
  retuneCompareOptions: HTMLElement;
  retuneChoiceOriginal: HTMLInputElement;
  retuneChoiceUpdated: HTMLInputElement;
  retuneChoiceOriginalLabel: HTMLElement;
  retuneChoiceUpdatedLabel: HTMLElement;
  retuneCompareMessage: HTMLElement;
  saveRetuneChoiceButton: HTMLButtonElement;
  devRetuneOriginalCenter: HTMLElement;
  devRetuneCoarsePeak: HTMLElement;
  devRetuneFinePeak: HTMLElement;
  devRetuneDelta: HTMLElement;

  diagnosticsFlowSection: HTMLElement;
  diagnosticsStatusText: HTMLElement;
  diagnosticsInstruction: HTMLElement;
  diagnosticsPrestartWarning: HTMLElement;
  diagnosticsHoldBanner: HTMLElement;
  diagnosticsSuccessMessage: HTMLElement;
  diagnosticsActionButtons: HTMLElement;
  startDiagnosticsButton: HTMLButtonElement;
  cancelDiagnosticsButton: HTMLButtonElement;
  diagnosticsHomeButton: HTMLButtonElement;
  diagnosticsProgress: HTMLElement;
  diagnosticsProgressBar: HTMLElement;
  diagnosticsProgressLabel: HTMLElement;
  diagnosticsCenterFrequency: HTMLElement;
  diagnosticsBatteryVoltage: HTMLElement;
  diagnosticsTemperature: HTMLElement;
  diagnosticsSummary: HTMLElement;
  downloadDiagnosticsButton: HTMLButtonElement;

  logList: HTMLElement;
  clearLogButton: HTMLButtonElement;
  downloadSupportLogButton: HTMLButtonElement;

  devControls: HTMLElement;
  devModeLive: HTMLInputElement;
  devModeSuccess: HTMLInputElement;
  devModeFail: HTMLInputElement;
  devRetuneModeLive: HTMLInputElement;
  devRetuneModeSuccess: HTMLInputElement;
  devRetuneModeFail: HTMLInputElement;
  devDiagnosticsModeLive: HTMLInputElement;
  devDiagnosticsModeSuccess: HTMLInputElement;
  devDiagnosticsModeFail: HTMLInputElement;
  devDiagnosticsObfuscationOn: HTMLInputElement;
  devDiagnosticsObfuscationOff: HTMLInputElement;
  devIdleTimeout5m: HTMLInputElement;
  devIdleTimeout15s: HTMLInputElement;
};

function requireElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`Missing element: ${id}`);
  }
  return element as T;
}

function requireElements<T extends HTMLElement>(selector: string): T[] {
  const elements = Array.from(document.querySelectorAll<T>(selector));
  if (!elements.length) {
    throw new Error(`Missing elements: ${selector}`);
  }
  return elements;
}

export function bindDom(): DomRefs {
  return {
    bluefyGate: requireElement('bluefyGate'),
    bluefyDownloadLink: requireElement('bluefyDownloadLink'),
    updaterShell: requireElement('updaterShell'),
    eulaGate: requireElement('eulaGate'),
    eulaContent: requireElement('eulaContent'),
    eulaAcceptCheckbox: requireElement('eulaAcceptCheckbox'),
    eulaAcceptButton: requireElement('eulaAcceptButton'),
    eulaDeclineButton: requireElement('eulaDeclineButton'),
    compatibilityPanel: requireElement('compatibilityPanel'),
    compatibilityMessage: requireElement('compatibilityMessage'),
    idleDisconnectHomeMessage: requireElement('idleDisconnectHomeMessage'),

    wizardSteps: requireElements('[data-step]'),
    taskWizardProgress: requireElement('taskWizardProgress'),
    wizardProgressFill: requireElement('wizardProgressFill'),
    wizardProgressLabel: requireElement('wizardProgressLabel'),
    wizardProgressTitle: requireElement('wizardProgressTitle'),

    deviceStatsPanel: requireElement('deviceStatsPanel'),
    connectButton: requireElement('connectButton'),
    deviceName: requireElement('deviceName'),
    deviceId: requireElement('deviceId'),
    deviceFirmware: requireElement('deviceFirmware'),
    deviceHardware: requireElement('deviceHardware'),
    deviceSerial: requireElement('deviceSerial'),
    deviceModel: requireElement('deviceModel'),
    deviceCenterFrequency: requireElement('deviceCenterFrequency'),
    devicePower: requireElement('devicePower'),
    deviceBattery: requireElement('deviceBattery'),
    deviceTemperature: requireElement('deviceTemperature'),
    deviceOdometer: requireElement('deviceOdometer'),

    flowFirmware: requireElement('flowFirmware'),
    flowRetune: requireElement('flowRetune'),
    flowDiagnostics: requireElement('flowDiagnostics'),
    flowFirmwareBody: requireElement('flowFirmwareBody'),

    taskPanelTitle: requireElement('taskPanelTitle'),
    taskPanelDescription: requireElement('taskPanelDescription'),

    statusText: requireElement('statusText'),

    firmwareFlowSection: requireElement('firmwareFlowSection'),
    firmwareSummary: requireElement('firmwareSummary'),
    firmwareList: requireElement('firmwareList'),
    firmwareEmpty: requireElement('firmwareEmpty'),
    startUpdateButton: requireElement('startUpdateButton'),
    cancelButton: requireElement('cancelButton'),
    reconnectPanel: requireElement('reconnectPanel'),
    reconnectButton: requireElement('reconnectButton'),
    progressSection: requireElement('progressSection'),
    progressBar: requireElement('progressBar'),
    progressMeta: requireElement('progressMeta'),
    progressPercent: requireElement('progressPercent'),
    progressSpeed: requireElement('progressSpeed'),
    progressDetail: requireElement('progressDetail'),

    retuneFlowSection: requireElement('retuneFlowSection'),
    retuneFirmwareBanner: requireElement('retuneFirmwareBanner'),
    retuneStatusText: requireElement('retuneStatusText'),
    retuneInstruction: requireElement('retuneInstruction'),
    retunePrestartWarning: requireElement('retunePrestartWarning'),
    retuneTargetPanel: requireElement('retuneTargetPanel'),
    retuneTargetSummary: requireElement('retuneTargetSummary'),
    retuneTargetOptions: requireElement('retuneTargetOptions'),
    retuneHoldBanner: requireElement('retuneHoldBanner'),
    retuneProgress: requireElement('retuneProgress'),
    retuneProgressBar: requireElement('retuneProgressBar'),
    retuneProgressLabel: requireElement('retuneProgressLabel'),
    retuneChartCard: requireElement('retuneChartCard'),
    retuneChartFrame: requireElement('retuneChartFrame'),
    retuneRuntimeRail: requireElement('retuneRuntimeRail'),
    retuneRuntimeProbeLayer: requireElement('retuneRuntimeProbeLayer'),
    retuneRuntimeRefineWindow: requireElement('retuneRuntimeRefineWindow'),
    retuneRuntimeCenterMarker: requireElement('retuneRuntimeCenterMarker'),
    retuneRuntimeCenterValue: requireElement('retuneRuntimeCenterValue'),
    retuneRuntimeRangeMin: requireElement('retuneRuntimeRangeMin'),
    retuneRuntimeRangeMax: requireElement('retuneRuntimeRangeMax'),
    retuneRuntimePhaseStatus: requireElement('retuneRuntimePhaseStatus'),
    retuneRuntimePhaseScanning: requireElement('retuneRuntimePhaseScanning'),
    retuneRuntimePhaseRefining: requireElement('retuneRuntimePhaseRefining'),
    retuneRuntimePhaseTuned: requireElement('retuneRuntimePhaseTuned'),
    retuneChartLegend: requireElement('retuneChartLegend'),
    retuneChartLegendOriginal: requireElement('retuneChartLegendOriginal'),
    retuneRuntimeLegend: requireElement('retuneRuntimeLegend'),
    retuneRuntimeLegendCenter: requireElement('retuneRuntimeLegendCenter'),
    retuneChartCanvas: requireElement('retuneChartCanvas'),
    retuneComparePanel: requireElement('retuneComparePanel'),
    retuneCompareInstruction: requireElement('retuneCompareInstruction'),
    retuneCompareOptions: requireElement('retuneCompareOptions'),
    retuneChoiceOriginal: requireElement('retuneChoiceOriginal'),
    retuneChoiceUpdated: requireElement('retuneChoiceUpdated'),
    retuneChoiceOriginalLabel: requireElement('retuneChoiceOriginalLabel'),
    retuneChoiceUpdatedLabel: requireElement('retuneChoiceUpdatedLabel'),
    retuneCompareMessage: requireElement('retuneCompareMessage'),
    saveRetuneChoiceButton: requireElement('saveRetuneChoiceButton'),
    devRetuneOriginalCenter: requireElement('devRetuneOriginalCenter'),
    devRetuneCoarsePeak: requireElement('devRetuneCoarsePeak'),
    devRetuneFinePeak: requireElement('devRetuneFinePeak'),
    devRetuneDelta: requireElement('devRetuneDelta'),

    diagnosticsFlowSection: requireElement('diagnosticsFlowSection'),
    diagnosticsStatusText: requireElement('diagnosticsStatusText'),
    diagnosticsInstruction: requireElement('diagnosticsInstruction'),
    diagnosticsPrestartWarning: requireElement('diagnosticsPrestartWarning'),
    diagnosticsHoldBanner: requireElement('diagnosticsHoldBanner'),
    diagnosticsSuccessMessage: requireElement('diagnosticsSuccessMessage'),
    diagnosticsActionButtons: requireElement('diagnosticsActionButtons'),
    startDiagnosticsButton: requireElement('startDiagnosticsButton'),
    cancelDiagnosticsButton: requireElement('cancelDiagnosticsButton'),
    diagnosticsHomeButton: requireElement('diagnosticsHomeButton'),
    diagnosticsProgress: requireElement('diagnosticsProgress'),
    diagnosticsProgressBar: requireElement('diagnosticsProgressBar'),
    diagnosticsProgressLabel: requireElement('diagnosticsProgressLabel'),
    diagnosticsCenterFrequency: requireElement('diagnosticsCenterFrequency'),
    diagnosticsBatteryVoltage: requireElement('diagnosticsBatteryVoltage'),
    diagnosticsTemperature: requireElement('diagnosticsTemperature'),
    diagnosticsSummary: requireElement('diagnosticsSummary'),
    downloadDiagnosticsButton: requireElement('downloadDiagnosticsButton'),

    logList: requireElement('logList'),
    clearLogButton: requireElement('clearLogButton'),
    downloadSupportLogButton: requireElement('downloadSupportLogButton'),

    devControls: requireElement('devControls'),
    devModeLive: requireElement('devModeLive'),
    devModeSuccess: requireElement('devModeSuccess'),
    devModeFail: requireElement('devModeFail'),
    devRetuneModeLive: requireElement('devRetuneModeLive'),
    devRetuneModeSuccess: requireElement('devRetuneModeSuccess'),
    devRetuneModeFail: requireElement('devRetuneModeFail'),
    devDiagnosticsModeLive: requireElement('devDiagnosticsModeLive'),
    devDiagnosticsModeSuccess: requireElement('devDiagnosticsModeSuccess'),
    devDiagnosticsModeFail: requireElement('devDiagnosticsModeFail'),
    devDiagnosticsObfuscationOn: requireElement('devDiagnosticsObfuscationOn'),
    devDiagnosticsObfuscationOff: requireElement('devDiagnosticsObfuscationOff'),
    devIdleTimeout5m: requireElement('devIdleTimeout5m'),
    devIdleTimeout15s: requireElement('devIdleTimeout15s'),
  };
}
